import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDate } from '../../utils/formatDate';
import styles from './DatePicker.module.css';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const Z_INDEX = 999999;

function parseISO(iso) {
    if (!iso || typeof iso !== 'string') return null;
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    return { year: Number(m[1]), month: Number(m[2]) - 1, day: Number(m[3]) };
}

function toISO(year, month, day) {
    const mm = String(month + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    return `${year}-${mm}-${dd}`;
}

function daysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
}

/**
 * Custom date picker — replaces native <input type="date"> so the app can show a consistent
 * "dd/MMM/yyyy" format everywhere instead of whatever the visitor's browser/OS locale would
 * otherwise render (native date inputs can't be restyled to a fixed format). Drop-in
 * compatible with the native input's contract: value/onChange/min/max are all ISO
 * "yyyy-mm-dd" strings, so callers don't need to change any surrounding validation logic.
 */
const DatePicker = ({ value, onChange, min, max, disabled = false, placeholder = 'Select date', id }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState(null);
    const selected = parseISO(value);
    const [viewYear, setViewYear] = useState(() => (selected || parseISO(min) || { year: new Date().getFullYear() }).year);
    const [viewMonth, setViewMonth] = useState(() => (selected || parseISO(min) || { month: new Date().getMonth() }).month);
    const triggerRef = useRef(null);
    const popoverRef = useRef(null);

    useLayoutEffect(() => {
        if (!isOpen || !triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({ left: rect.left, top: rect.bottom + 6, width: rect.width });
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const handlePointer = (e) => {
            if (triggerRef.current?.contains(e.target) || popoverRef.current?.contains(e.target)) return;
            setIsOpen(false);
        };
        const handleKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
        document.addEventListener('mousedown', handlePointer);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handlePointer);
            document.removeEventListener('keydown', handleKey);
        };
    }, [isOpen]);

    const openPicker = () => {
        if (disabled) return;
        const anchor = selected || parseISO(min) || { year: new Date().getFullYear(), month: new Date().getMonth() };
        setViewYear(anchor.year);
        setViewMonth(anchor.month);
        setIsOpen((v) => !v);
    };

    const goMonth = (delta) => {
        let m = viewMonth + delta;
        let y = viewYear;
        if (m < 0) { m = 11; y -= 1; }
        else if (m > 11) { m = 0; y += 1; }
        setViewMonth(m);
        setViewYear(y);
    };

    const pick = (day) => {
        const iso = toISO(viewYear, viewMonth, day);
        onChange?.(iso);
        setIsOpen(false);
    };

    const total = daysInMonth(viewYear, viewMonth);
    const firstWeekday = new Date(viewYear, viewMonth, 1).getDay();
    const prevMonthTotal = daysInMonth(viewYear, viewMonth === 0 ? 11 : viewMonth - 1);

    const cells = [];
    for (let i = 0; i < firstWeekday; i++) {
        cells.push({ day: prevMonthTotal - firstWeekday + 1 + i, outside: true });
    }
    for (let d = 1; d <= total; d++) {
        cells.push({ day: d, outside: false });
    }
    while (cells.length % 7 !== 0) {
        cells.push({ day: cells.length - (firstWeekday + total) + 1, outside: true });
    }

    const todayISO = toISO(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                id={id}
                className={styles.trigger}
                onClick={openPicker}
                disabled={disabled}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
            >
                <Calendar size={17} className={styles.triggerIcon} aria-hidden />
                <span className={value ? styles.triggerValue : styles.triggerPlaceholder}>
                    {value ? formatDate(value) : placeholder}
                </span>
            </button>

            {isOpen && createPortal(
                <div
                    ref={popoverRef}
                    className={styles.popover}
                    role="dialog"
                    aria-label="Choose a date"
                    style={{ position: 'fixed', left: position?.left ?? 0, top: position?.top ?? 0, zIndex: Z_INDEX }}
                >
                    <div className={styles.header}>
                        <button type="button" className={styles.navBtn} onClick={() => goMonth(-1)} aria-label="Previous month">
                            <ChevronLeft size={16} />
                        </button>
                        <span className={styles.headerLabel}>{MONTH_NAMES[viewMonth]} {viewYear}</span>
                        <button type="button" className={styles.navBtn} onClick={() => goMonth(1)} aria-label="Next month">
                            <ChevronRight size={16} />
                        </button>
                    </div>
                    <div className={styles.weekdays}>
                        {WEEKDAY_LABELS.map((w, i) => (
                            <span key={i} className={styles.weekday}>{w}</span>
                        ))}
                    </div>
                    <div className={styles.grid}>
                        {cells.map((cell, i) => {
                            const iso = cell.outside ? null : toISO(viewYear, viewMonth, cell.day);
                            const isSelected = !cell.outside && iso === value;
                            const isToday = !cell.outside && iso === todayISO;
                            const isDisabled = !cell.outside && ((min && iso < min) || (max && iso > max));
                            return (
                                <button
                                    key={i}
                                    type="button"
                                    className={`${styles.day} ${cell.outside ? styles.dayOutside : ''} ${isSelected ? styles.daySelected : ''} ${isToday && !isSelected ? styles.dayToday : ''}`}
                                    disabled={cell.outside || isDisabled}
                                    onClick={() => pick(cell.day)}
                                    tabIndex={cell.outside ? -1 : 0}
                                >
                                    {cell.day}
                                </button>
                            );
                        })}
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default DatePicker;
